import { TWidgetCommonOption, TRowComponentProps, TWidget, TCommonData } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, ChangeEvent, MouseEvent, MouseEventHandler } from 'react';
import { TRowOption, TSelectorRegularOption } from '../../../types/options';
import { getInlineStyles } from '../../../utils/styles';
import { TextCommon } from '../Text/TextCommon';
import { SelectorRegularLeftIcon } from './SelectorRegularLeftIcon';
import { SelectorRegularRight } from './SelectorRegularRight';
import styles from './styles/rowRegular.module.css';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

export type TSelectorRegularViewProps = {
    /**
     * Optional server-provided widget. When supplied, widget fields take priority over
     * the corresponding explicit props (uid, title, subtitle, etc.).
     * When omitted, all values are taken from the explicit props directly.
     */
    uid?: TWidget<TWidgetCommonOption>['uid'];
    widget?: TWidget<TCommonData>;
    title?: string;
    subtitle?: string;
    metaText?: string;
    variant?: TSelectorRegularOption['variant'];
    margin?: TWidgetCommonOption['margin'];
    isDisabled?: boolean;
    isHide?: boolean;
    handleClick: ((e: MouseEvent | ChangeEvent) => void) | MouseEventHandler<HTMLButtonElement>;
} & Pick<TRowComponentProps, 'isDisabled' | 'isHide'> &
    Pick<TRowOption, 'iconName' | 'flagIcon' | 'avatar' | 'iconHierarchy' | 'iconVariant' | 'metaVariant' | 'metaHierarchy' | 'isSelected'>;

/**
 * Composable view for the SelectorRegular row widget.
 * Owns only rendering logic and accepts pre-resolved behavior props
 * (isDisabled, isHide, handleClick) from the parent.
 *
 * `widget` is optional — when omitted, individual explicit props (uid, title,
 * subtitle, value, etc.) are used directly. This makes the view suitable for
 * both server-driven usage (via SelectorRegularWidget) and standalone composable
 * usage (e.g. inside list row components).
 */
export const SelectorRegularView = ({
    uid,
    title,
    subtitle,
    metaText,
    iconName,
    flagIcon,
    avatar,
    isSelected,
    iconHierarchy,
    iconVariant,
    metaVariant,
    metaHierarchy,
    variant,
    margin,
    isDisabled,
    isHide,
    handleClick,
}: TSelectorRegularViewProps) => {
    const resolvedVariant = variant || 'CHECK';

    return (
        <Activity mode={isHide ? 'hidden' : 'visible'}>
            <button
                data-test={uid}
                className={clsx(styles.commonRowRegular, isDisabled && styles.disabled)}
                style={getInlineStyles({ margin })}
                onClick={handleClick as MouseEventHandler<HTMLButtonElement>}
                aria-hidden={isHide}
                aria-label={title}
                aria-disabled={isDisabled}
                disabled={isDisabled}
            >
                <SelectorRegularLeftIcon
                    variant={resolvedVariant}
                    iconName={iconName}
                    iconHierarchy={iconHierarchy}
                    iconVariant={iconVariant}
                    avatar={avatar}
                    flagIcon={flagIcon}
                    isDisabled={isDisabled}
                />
                <div className={styles.rowContent}>
                    {title && <div className={clsx(styles.highlightedTitle, isDisabled && styles.disabled)}>{parse(DOMPurify.sanitize(title))}</div>}
                    {subtitle && (
                        <TextCommon
                            isWidget={false}
                            content={subtitle}
                            as={'span'}
                            options={{
                                variant: 'secondary',
                                textColor: isDisabled ? 'grey_40_lc' : 'grey_72_lc',
                                margin: { top: 4 },
                            }}
                        />
                    )}
                </div>
                <SelectorRegularRight
                    uid={`right_selector_regular_${uid}`}
                    handleChange={handleClick as (e: React.ChangeEvent<HTMLInputElement>) => void}
                    isDisabled={isDisabled}
                    value={!!isSelected}
                    metaText={metaText}
                    metaVariant={metaVariant}
                    metaHierarchy={metaHierarchy}
                    variant={resolvedVariant}
                />
            </button>
        </Activity>
    );
};
